import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApiServerService } from '../../shared/services/api-server.service';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { AdOrderDraftActions } from '../actions/ad-order-draft.action';
import { catchError, debounceTime, filter, map, of, switchMap, withLatestFrom } from 'rxjs';
import { selectAdOrderItems, selectAdPublication, selectTotalPrice } from '../selectors/ad-order-draft.selectors';
import { AdOrderDraftUpsertResponseDto } from '@smmof/dto';
import { AdOrderDraft } from '@smmof/entity';

@Injectable()
export class AdOrderDraftEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private apiServerService: ApiServerService,
    // private store: Store<AdvertiserPlatformsState>,
    private router: Router
  ) {}

  /** *
   * Апсерт черновика: при инициализации и любых изменениях черновика
   *
   */
  upsertDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AdOrderDraftActions.upsertRequested,
        AdOrderDraftActions.addPlatformToAdOrderDraft,
        AdOrderDraftActions.updateOrderItemSettings,
        AdOrderDraftActions.removePlatformFromAdOrderDraft,
        AdOrderDraftActions.updateAdPublication
      ),
      debounceTime(600),
      withLatestFrom(
        this.store.select(selectAdOrderItems),
        this.store.select(selectAdPublication),
        this.store.select(selectTotalPrice),
      ),
      map(([_, items, adPublication, totalPrice]) => {
        const req = {
          adPublication,
          items: (items || []).map(i => ({
            platformId: i.platform.id,
            settings: i.settings,
          })),
          totalPrice: totalPrice || 0,
        };
        return req;
      }),
      filter(req => (req.items?.length ?? 0) > 0 || !!req.adPublication),
      switchMap((req) =>
          this.apiServerService.POST<AdOrderDraftUpsertResponseDto>('ad-order-drafts/upsert', req).pipe(
            map(res => AdOrderDraftActions.upsertSuccess({ serverDraftId: res.id, updatedAt: res.updatedAt })),
            catchError(error => of(AdOrderDraftActions.upsertFailure({ error })))
        )
      )
    )
  );

  /** *
   * Загрузка единственного черновика пользователя (для синхронизации между устройствами)
   *
   */
  loadMyDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdOrderDraftActions.loadAdOrderDraft),
      withLatestFrom(this.store.select((s: any) => s?.adOrderDraft?.updatedAt ?? null)),
      switchMap(([_, localUpdatedAt]) =>
        this.apiServerService.GET<AdOrderDraft>('ad-order-drafts/me').pipe(
          map((serverDraft) => {
            if (!serverDraft) {
              return AdOrderDraftActions.loadAdOrderDraftFailure({ error: null });
            }
            const serverTs = new Date(serverDraft.updatedAt || 0).getTime();
            const localTs = new Date(localUpdatedAt || 0).getTime();
            if (!localTs || serverTs > localTs + 1000) {
              return AdOrderDraftActions.loadAdOrderDraftSuccess({ draft: serverDraft });
            }
            return AdOrderDraftActions.loadAdOrderDraftFailure({ error: null });
          }),
          catchError((error) => of(AdOrderDraftActions.loadAdOrderDraftFailure({ error })))
        )
      )
    )
  );

  navigateToOrder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdOrderDraftActions.createAdOrderDraft),
        switchMap(() => this.router.navigate([`/advertiser/ad-order/create`]))
      ),
    { dispatch: false }
  );
}
